<template>
  <FullHeightDrawer ref="fullHeightDrawerRef" :on-close="closePanel" close-icon-position="none">
    <TabGroup :onChange="onTabChanged" :selectedIndex="selectedTab">
      <div class="tab-list-scroll-container">
        <TabList
          v-if="!store.isBackgroundSearchActive"
          class="flex space-x-2 !pl-3 md:!pl-0 md:!pt-4 md:!pb-2 relative z-9"
          key="tabList"
        >
          <Tab v-if="isMagicBackgroundsEnabled" v-slot="{ selected }" as="template">
            <Button variant="ghost" :active="selected" class="whitespace-nowrap text-sm">
              {{ I18n.t("editor.magic") }}
            </Button>
          </Tab>
          <Tab v-slot="{ selected }" as="template">
            <Button variant="ghost" :active="selected" class="text-sm">{{ I18n.t("editor.photo") }}</Button>
          </Tab>
          <Tab v-slot="{ selected }" as="template">
            <Button variant="ghost" :active="selected" class="text-sm">{{ I18n.t("editor.color") }}</Button>
          </Tab>
        </TabList>
      </div>

      <TabPanels key="panels">
        <!-- Start AI Generate -->

        <TabPanel>
          <InspirationGallery
            :foreground-type="props.foregroundType"
            :selected-image-state="props.selectedImageState"
            :scroll-container-height="scrollContainerHeight"
          />
        </TabPanel>

        <!-- Start Photo -->

        <TabPanel>
          <BackgroundImageSearch
            :scroll-container-height="scrollContainerHeight"
            :foreground-type="props.foregroundType"
            :selected-image-state="props.selectedImageState"
          />
        </TabPanel>

        <!-- End Photo -->

        <!-- Start Color -->
        <TabPanel>
          <div class="divider"><hr /></div>
          <div class="scroll-container">
            <div class="sm:h-64 pb-4 gap-1 tile-grid-photos-new-layout">
              <ColorTiles
                :selected-image-state="props.selectedImageState"
                :colors="availableColors"
                image-button-size="flex"
              />
            </div>
          </div>
        </TabPanel>
        <!-- End Color -->
      </TabPanels>
    </TabGroup>
  </FullHeightDrawer>
</template>

<script setup lang="ts">
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/vue";
import { Button } from "prism";
import ColorTiles from "@/components/prism/color_tiles.vue";
import BackgroundImageSearch from "@/components/upload/add_background/background_image_search.vue";
import { ref, inject, computed } from "vue";
import { PersistentStore } from "@/stores/persistent_store";
import { useEditorStore } from "@/stores/editor_store";
import InspirationGallery from "@/components/upload/ai_generate/inspiration_gallery.vue";
import { rbgEditorBackgroundPanelTabClickedV100 } from "kaleido-event-tracker";
import FullHeightDrawer from "./full_height_drawer.vue";
import { I18n as I18nType } from "@/src/i18n";
import { useMagicBackgroundFlag } from "@/composables/magic_background_flag";

const I18n = inject<I18nType>("I18n");
const fullHeightDrawerRef = ref<InstanceType<typeof FullHeightDrawer>>();

const store = useEditorStore();

export type AddBackgroundTabs = "ai_scene" | "photo" | "color";

interface AddBackgroundProps {
  foregroundType: string;
  selectedImageState: PersistentStore;
}

const tabIndexToName: Record<number, AddBackgroundTabs> = {
  0: "ai_scene",
  1: "photo",
  2: "color",
};

const nameToTabIndex = (tabName: string) => {
  const tabIndex = Number(Object.entries(tabIndexToName).find(([_index, name]) => name === tabName)?.[0] ?? 0);
  return isNaN(tabIndex) ? 0 : tabIndex;
};

const selectedTab = ref(nameToTabIndex(store.openedBackgroundTab));
const props = defineProps<AddBackgroundProps>();
const availableColors = ref<string[]>([
  "#ffffff",
  "#f44336",
  "#e91e63",
  "#9c27b0",
  "#673ab7",
  "#3f51b5",
  "#2196f3",
  "#03a9f4",
  "#00bcd4",
  "#009688",
  "#4caf50",
  "#8bc34a",
  "#cddc39",
  "#ffeb3b",
  "#ffc107",
  "#ff9800",
  "#ff5722",
  "#795548",
  "#9e9e9e",
  "#607d8b",
  "#000000",
]);

// The spacing is the height of the tab list items in the drawer background panel
const spacing = 72;
const getScrollContainerHeight = (drawerHeight: number) => {
  const minHeight = 368; // almost eq. to 23rem
  const height = drawerHeight && drawerHeight > minHeight ? drawerHeight : minHeight;
  return height - spacing;
};
const scrollContainerHeightInPx = computed(() => {
  const drawerHeight = fullHeightDrawerRef.value?.drawerHeight;
  const paddingSpace = 10; // Padding value that's calculated to match other panels
  return `${getScrollContainerHeight(drawerHeight) - paddingSpace}px`;
});

const scrollContainerHeight = computed(() => {
  const drawerHeight = fullHeightDrawerRef.value?.drawerHeight;
  return getScrollContainerHeight(drawerHeight);
});

const emit = defineEmits<{
  (e: "closePanel"): void;
}>();

const closePanel = () => {
  emit("closePanel");
};
const { isMagicBackgroundsEnabled } = useMagicBackgroundFlag();

const onTabChanged = (index: number) => {
  selectedTab.value = index;
  const tabName = tabIndexToName[index];
  store.openedBackgroundTab = tabName;
  if (isMagicBackgroundsEnabled.value) {
    rbgEditorBackgroundPanelTabClickedV100({ image_id: store.selectedImage.meta.id, panel_tab_name: tabName });
  }
};
</script>

<style scoped>
.tile-grid-photos-new-layout {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));

  @media (min-width: 368px) and (max-width: 767px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.tab-list-scroll-container {
  overflow-x: auto;
}

.slide-move {
  transition: all 0.15s ease;
}
.slide-leave-active {
  position: absolute;
}

.divider {
  margin: 0 -16px !important;

  @media (min-width: 640px) {
    margin: 0 -24px !important;
  }
}

.scroll-container {
  overflow-x: hidden;
  overflow-y: auto;
  height: calc(v-bind(scrollContainerHeightInPx) - 32px);
}

/* this feature is only supported on ios, it's used here because scroll behavior is different on ios and negative margin shifts the content */
/* The following css has side effects on color tab of background panel; disabling it for now */
/* @supports not (-webkit-touch-callout: none) {
  .scroll-container {
    margin-right: -1rem;
  }
} */

/* .scroll-container::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 1rem;
} */

.tab-list-scroll-container::-webkit-scrollbar {
  -webkit-appearance: none;
  height: 0;
}

.scroll-container::-webkit-scrollbar-thumb {
  border: 4px solid rgba(0, 0, 0, 0);
  background-clip: padding-box;
  -webkit-border-radius: 7px;
  background-color: rgba(0, 0, 0, 0.15);
  -webkit-box-shadow: inset -1px -1px 0px rgba(0, 0, 0, 0.05), inset 1px 1px 0px rgba(0, 0, 0, 0.05);
}

.z-9 {
  z-index: 9;
}
</style>
